/**
 * My Care Team — plug in your ACTUAL providers (GUIDE program, home
 * health agency, neurologist...) and they populate on your Roadmap as
 * merge-on / merge-off lanes.
 *
 * Statuses follow the road metaphor:
 *   active         → "On the road with you"
 *   starting_soon  → "Merging on soon"
 *   ended          → "Merged off" — unlocks transition guidance
 *
 * Supports ?add=<service_key> deep-links from the Roadmap's service
 * cards ("Add yours") so the form opens pre-selected.
 */

import { PageHero } from "@/components/PageHero";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { SERVICES } from "@/lib/roadmapStages";
import { track, EVENTS } from "@/lib/analytics";
import ShareUpdateDialog from "@/components/ShareUpdateDialog";
import { toast } from "sonner";
import {
    Users, Plus, Phone, User, Pencil, Trash2, LogOut, LogIn,
    Sparkles, MessageCircle, ArrowRight, Map, CircleDot, X,
} from "lucide-react";

const STATUS_META = {
    active: {
        label: "On the road with you",
        chip: "bg-teal-soft text-teal-dark",
        dot: "bg-teal",
    },
    referred: {
        label: "Referred",
        chip: "bg-[#DFE8FC] text-[#2D62D9]",
        dot: "bg-[#2D62D9]",
    },
    paused: {
        label: "Paused",
        chip: "bg-stage_middle text-stage_middle-dark",
        dot: "bg-stage_middle-dark",
    },
    ended: {
        label: "Merged off",
        chip: "bg-cream-dark/60 text-[#4C5551]",
        dot: "bg-[#9CA6A1]",
    },
};

// Legacy status from before referred/paused existed.
const normalizeStatus = (s) => (s === "starting_soon" ? "referred" : s);

const SERVICE_OPTIONS = [
    ...Object.entries(SERVICES).map(([key, svc]) => ({ key, name: svc.name })),
    { key: "other", name: "Other service" },
];

const EMPTY_FORM = {
    service_key: "",
    name: "",
    phone: "",
    contact_person: "",
    notes: "",
    status: "active",
};

export default function CareTeam() {
    const sid = getSessionId();
    const [searchParams, setSearchParams] = useSearchParams();
    const [shareUpdateOpen, setShareUpdateOpen] = useState(false);
    const [providers, setProviders] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null); // provider being edited, or null
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;
        api.get(`/care-team/providers/${sid}`)
            .then((r) => { if (active) setProviders(r.data?.providers || []); })
            .catch(() => { })
            .finally(() => { if (active) setLoaded(true); });
        return () => { active = false; };
    }, [sid]);

    // Deep-link: /care-team?add=home_health opens the form pre-selected.
    useEffect(() => {
        const addKey = searchParams.get("add");
        if (!addKey) return;
        setForm({ ...EMPTY_FORM, service_key: SERVICES[addKey] ? addKey : "other" });
        setEditing(null);
        setFormOpen(true);
        setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const groups = useMemo(() => ({
        active: providers.filter(p => normalizeStatus(p.status) === "active"),
        referred: providers.filter(p => normalizeStatus(p.status) === "referred"),
        paused: providers.filter(p => normalizeStatus(p.status) === "paused"),
        ended: providers.filter(p => normalizeStatus(p.status) === "ended"),
    }), [providers]);

    const openAdd = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            service_key: p.service_key || "other",
            name: p.name || "",
            phone: p.phone || "",
            contact_person: p.contact_person || "",
            notes: p.notes || "",
            status: normalizeStatus(p.status) || "active",
        });
        setFormOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Give this provider a name."); return; }
        if (!form.service_key) { toast.error("Pick what kind of service this is."); return; }
        setSaving(true);
        try {
            if (editing) {
                const r = await api.put(`/care-team/providers/${sid}/${editing.id}`, form);
                setProviders(prev => prev.map(p => p.id === editing.id ? r.data.provider : p));
                toast.success("Provider updated.");
            } else {
                const r = await api.post(`/care-team/providers/${sid}`, form);
                setProviders(prev => [r.data.provider, ...prev]);
                track(EVENTS.CARE_PROVIDER_ADDED, { service_key: form.service_key, status: form.status });
                toast.success(`${form.name} is on your road.`);
            }
            setFormOpen(false);
            setEditing(null);
            setForm(EMPTY_FORM);
        } catch {
            toast.error("Couldn't save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const markEnded = async (p) => {
        try {
            const r = await api.put(`/care-team/providers/${sid}/${p.id}`, { status: "ended" });
            setProviders(prev => prev.map(x => x.id === p.id ? r.data.provider : x));
            track(EVENTS.CARE_PROVIDER_ENDED, { service_key: p.service_key });
            toast.success(`${p.name} marked as merged off. Transition guidance is below.`);
        } catch {
            toast.error("Couldn't update. Please try again.");
        }
    };

    const backOnRoad = async (p) => {
        try {
            const r = await api.put(`/care-team/providers/${sid}/${p.id}`, { status: "active" });
            setProviders(prev => prev.map(x => x.id === p.id ? r.data.provider : x));
            toast.success(`${p.name} is back on the road.`);
        } catch {
            toast.error("Couldn't update. Please try again.");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Remove ${p.name} from your care team?`)) return;
        try {
            await api.delete(`/care-team/providers/${sid}/${p.id}`);
            setProviders(prev => prev.filter(x => x.id !== p.id));
            toast.success("Removed.");
        } catch {
            toast.error("Couldn't remove. Please try again.");
        }
    };

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="care-team-page">
            <PageHero
                testId="care-team-hero"
                kicker="Who's on the road with you"
                kickerIcon={Users}
                title="Care Team"
                titleTestId="care-team-title"
                subtitle={<>
                    Plug in your real providers — your GUIDE program, your home health agency, your neurologist —
                    and they&apos;ll appear on your <Link to="/roadmap" className="text-teal-dark underline underline-offset-2">Roadmap</Link> as
                    lanes that merge on and off alongside you.
                </>}
                actions={
                    <button type="button" onClick={openAdd} className="ml-btn-primary text-sm !min-h-[44px] !py-2.5" data-testid="care-team-add-btn">
                        <Plus size={16} strokeWidth={2} /> Add a provider
                    </button>
                }
            />

            {/* Sharing + support actions that live under Care Team now */}
            <div className="flex gap-2 flex-wrap" data-testid="care-team-quick-links">
                <button type="button" onClick={() => setShareUpdateOpen(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="care-team-link-share-update">
                    <ArrowRight size={14} strokeWidth={1.8} /> Share an update with a provider
                </button>
                <Link to="/family" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="care-team-link-family">
                    <Users size={14} strokeWidth={1.8} /> Share with family
                </Link>
                <Link to="/navigator" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="care-team-link-navigator">
                    <Sparkles size={14} strokeWidth={1.8} /> Book a Care Navigator session
                </Link>
            </div>

            <ConnectedOrgsPrompt />

            <ShareUpdateDialog open={shareUpdateOpen} onClose={() => setShareUpdateOpen(false)} />

            {formOpen && (
                <ProviderFormCard
                    form={form}
                    setForm={setForm}
                    editing={editing}
                    saving={saving}
                    onSubmit={submit}
                    onCancel={() => { setFormOpen(false); setEditing(null); }}
                />
            )}

            {loaded && providers.length === 0 && !formOpen && (
                <section className="ml-card p-8 md:p-10 text-center" data-testid="care-team-empty">
                    <span className="inline-flex w-14 h-14 rounded-2xl bg-teal-soft items-center justify-center mb-4">
                        <Map size={24} strokeWidth={1.6} className="text-teal-dark" />
                    </span>
                    <h2 className="text-xl font-heading font-medium text-[#1F2624]">Your road is waiting for its lanes.</h2>
                    <p className="mt-2 text-sm text-[#4C5551] max-w-md mx-auto leading-relaxed">
                        Add the providers already helping you — even just one. They&apos;ll show up on your roadmap,
                        and when one ends, we&apos;ll walk you through what usually comes next.
                    </p>
                    <button type="button" onClick={openAdd} className="ml-btn-primary mt-5 inline-flex" data-testid="care-team-empty-add-btn">
                        <Plus size={16} strokeWidth={2} /> Add your first provider
                    </button>
                </section>
            )}

            <StatusGroup
                status="active"
                providers={groups.active}
                onEdit={openEdit}
                onMarkEnded={markEnded}
                onRemove={remove}
            />
            <StatusGroup
                status="referred"
                providers={groups.referred}
                onEdit={openEdit}
                onMarkEnded={markEnded}
                onRemove={remove}
            />
            <StatusGroup
                status="paused"
                providers={groups.paused}
                onEdit={openEdit}
                onMarkEnded={markEnded}
                onRemove={remove}
            />
            <StatusGroup
                status="ended"
                providers={groups.ended}
                onEdit={openEdit}
                onBackOnRoad={backOnRoad}
                onRemove={remove}
            />
        </div>
    );
}

function ProviderFormCard({ form, setForm, editing, saving, onSubmit, onCancel }) {
    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
    const inputCls = "w-full rounded-xl border border-cream-dark bg-white px-3.5 py-2.5 text-sm text-[#1F2624] focus:outline-none focus:ring-2 focus:ring-teal/40";
    return (
        <form onSubmit={onSubmit} className="ml-card p-5 md:p-6 bg-cream-light/60 space-y-4" data-testid="care-team-provider-form">
            <div className="flex items-center justify-between">
                <h2 className="font-heading font-medium text-lg text-[#1F2624]">
                    {editing ? `Edit ${editing.name}` : "Add a provider to your road"}
                </h2>
                <button type="button" onClick={onCancel} className="text-[#6E7A74] hover:text-[#1F2624]" data-testid="provider-form-close">
                    <X size={18} strokeWidth={2} />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Type of service *</span>
                    <select
                        value={form.service_key}
                        onChange={set("service_key")}
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-service-key"
                    >
                        <option value="">Choose one…</option>
                        {SERVICE_OPTIONS.map(o => (
                            <option key={o.key} value={o.key}>{o.name}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Provider / agency name *</span>
                    <input
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="e.g., BrightStar Home Health"
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-name"
                    />
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Phone</span>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="(555) 123-4567"
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-phone"
                    />
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Contact person</span>
                    <input
                        type="text"
                        value={form.contact_person}
                        onChange={set("contact_person")}
                        placeholder="e.g., Maria, care coordinator"
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-contact-person"
                    />
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Status</span>
                    <select
                        value={form.status}
                        onChange={set("status")}
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-status"
                    >
                        <option value="active">On the road with you (active)</option>
                        <option value="referred">Referred (appointment pending)</option>
                        <option value="paused">Paused</option>
                        <option value="ended">Merged off (ended)</option>
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Notes</span>
                    <input
                        type="text"
                        value={form.notes}
                        onChange={set("notes")}
                        placeholder="e.g., Tues/Thurs 9–1, ask for Maria"
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="provider-form-notes"
                    />
                </label>
            </div>
            <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving} className="ml-btn-primary" data-testid="provider-form-submit">
                    {saving ? "Saving…" : editing ? "Save changes" : "Add to my road"}
                </button>
                <button type="button" onClick={onCancel} className="ml-btn-secondary" data-testid="provider-form-cancel">
                    Cancel
                </button>
            </div>
        </form>
    );
}

function StatusGroup({ status, providers, onEdit, onMarkEnded, onBackOnRoad, onRemove }) {
    if (!providers.length) return null;
    const meta = STATUS_META[status];
    const Icon = status === "ended" ? LogOut : status === "starting_soon" ? LogIn : CircleDot;
    return (
        <section data-testid={`care-team-group-${status}`}>
            <h2 className="text-xs uppercase tracking-wider font-semibold text-[#4C5551] mb-3 flex items-center gap-2">
                <Icon size={14} strokeWidth={2} />
                {meta.label} · {providers.length}
            </h2>
            <div className="space-y-3">
                {providers.map(p => (
                    <ProviderCard
                        key={p.id}
                        provider={p}
                        onEdit={onEdit}
                        onMarkEnded={onMarkEnded}
                        onBackOnRoad={onBackOnRoad}
                        onRemove={onRemove}
                    />
                ))}
            </div>
            {status === "ended" && (
                <p className="mt-2 text-xs text-[#6E7A74] italic">
                    When a service merges off, the road keeps going — the guidance above each one shows what usually comes next.
                </p>
            )}
        </section>
    );
}

function ProviderCard({ provider: p, onEdit, onMarkEnded, onBackOnRoad, onRemove }) {
    const meta = STATUS_META[normalizeStatus(p.status)] || STATUS_META.active;
    const typeName = SERVICES[p.service_key]?.name || p.service_name || "Other service";
    return (
        <article className="ml-card p-4 md:p-5" data-testid={`provider-card-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 rounded-2xl px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${meta.chip}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                        </span>
                        <span className="text-xs text-[#6E7A74]">{typeName}</span>
                    </div>
                    <h3 className="mt-1.5 text-lg font-heading font-medium text-[#1F2624]" data-testid={`provider-name-${p.id}`}>
                        {p.name}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#4C5551]">
                        {p.contact_person && (
                            <span className="inline-flex items-center gap-1.5">
                                <User size={13} strokeWidth={1.8} /> {p.contact_person}
                            </span>
                        )}
                        {p.phone && (
                            <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1.5 text-teal-dark hover:underline" data-testid={`provider-phone-${p.id}`}>
                                <Phone size={13} strokeWidth={1.8} /> {p.phone}
                            </a>
                        )}
                    </div>
                    {p.notes && <p className="mt-1.5 text-sm text-[#4C5551] italic">{p.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {p.status !== "ended" && onMarkEnded && (
                        <button
                            type="button"
                            onClick={() => onMarkEnded(p)}
                            className="ml-btn-secondary !px-3 !py-1.5 text-xs"
                            data-testid={`provider-mark-ended-${p.id}`}
                            title="This service has ended — merge it off the road"
                        >
                            <LogOut size={13} strokeWidth={2} /> Merge off
                        </button>
                    )}
                    {p.status === "ended" && onBackOnRoad && (
                        <button
                            type="button"
                            onClick={() => onBackOnRoad(p)}
                            className="ml-btn-secondary !px-3 !py-1.5 text-xs"
                            data-testid={`provider-back-on-road-${p.id}`}
                        >
                            <LogIn size={13} strokeWidth={2} /> Back on the road
                        </button>
                    )}
                    <button type="button" onClick={() => onEdit(p)} className="p-2 rounded-lg text-[#6E7A74] hover:bg-cream-light hover:text-[#1F2624]" data-testid={`provider-edit-${p.id}`}>
                        <Pencil size={15} strokeWidth={1.8} />
                    </button>
                    <button type="button" onClick={() => onRemove(p)} className="p-2 rounded-lg text-[#6E7A74] hover:bg-coral/10 hover:text-coral" data-testid={`provider-delete-${p.id}`}>
                        <Trash2 size={15} strokeWidth={1.8} />
                    </button>
                </div>
            </div>

            {p.status === "ended" && p.transition_guidance && (
                <TransitionGuidance guidance={p.transition_guidance} providerId={p.id} />
            )}
        </article>
    );
}

function TransitionGuidance({ guidance, providerId }) {
    return (
        <div
            className="mt-4 rounded-2xl bg-teal-soft/50 border border-teal-light/50 p-4"
            data-testid={`provider-transition-guidance-${providerId}`}
        >
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-2">
                <Sparkles size={13} strokeWidth={2} /> What usually comes next
            </p>
            <p className="mt-1.5 text-sm font-medium text-[#1F2624]">{guidance.headline}</p>
            <ul className="mt-2 space-y-1.5">
                {(guidance.next_steps || []).map((step, i) => (
                    <li key={i} className="flex gap-2 text-sm text-[#1F2624] leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0" />
                        <span>{step}</span>
                    </li>
                ))}
            </ul>
            {guidance.lane_prompt && (
                <Link
                    to={`/chat?prompt=${encodeURIComponent(guidance.lane_prompt)}`}
                    className="ml-btn-primary mt-3 inline-flex text-xs !px-3 !py-1.5"
                    data-testid={`provider-transition-talk-to-lane-${providerId}`}
                >
                    <MessageCircle size={13} strokeWidth={2} /> Talk to Lane about this transition <ArrowRight size={12} strokeWidth={2} />
                </Link>
            )}
        </div>
    );
}

/** Connected organizations — status prompt; full controls live in Settings. */
function ConnectedOrgsPrompt() {
    const [conns, setConns] = useState(null);
    useEffect(() => {
        let on = true;
        api.get("/care-access/mine")
            .then(r => on && setConns((r.data.connections || []).filter(c => c.status !== "disconnected")))
            .catch(() => on && setConns([]));
        return () => { on = false; };
    }, []);
    if (conns === null) return null;
    return (
        <section className="ml-card p-5 bg-white" data-testid="care-team-access-prompt">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-semibold text-[#1F2624]">Connected organizations</p>
                    {conns.length ? (
                        <p className="text-xs text-[#4C5551] mt-1">
                            {conns.map(c => {
                                const n = Object.values(c.permissions || {}).filter(Boolean).length;
                                return `${c.org_name || "Organization"} — ${c.status === "paused" ? "paused" : `sharing ${n} item${n === 1 ? "" : "s"}`}`;
                            }).join(" · ")}
                        </p>
                    ) : (
                        <p className="text-xs text-[#4C5551] mt-1">
                            No organizations connected yet. You own your record — when your GUIDE program or clinic joins Memory Lane, you choose exactly what they can see.
                        </p>
                    )}
                </div>
                <Link to="/settings#care-team-access" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-semibold bg-teal text-white hover:bg-teal-dark transition-colors" data-testid="care-team-manage-access-link">
                    Manage access
                </Link>
            </div>
        </section>
    );
}
